import { CategoryContext } from "@database/contexts";
import { categoryService } from "@database/services";
import { useCallback, useContext, useState } from "react";
import { useOrganisation } from "./useOrganisation";
import { ErrorType, CreateCategoryType } from "@utilities/types";

export const useCategory = () => {
    
    const { 
        categories, setCategories,
    } = useContext(CategoryContext);
    const { organisation } = useOrganisation();

    const [getAllOrganisationCategoriesLoading, setGetAllOrganisationCategoriesLoading] = useState<boolean>(false);
    const [createCategoryLoading, setCreateCategoryLoading] = useState<boolean>(false);
    const [updateCategoryLoading, setUpdateCategoryLoading] = useState<boolean>(false);
    const [deleteCategoryLoading, setDeleteCategoryLoading] = useState<boolean>(false);
    const [getAllOrganisationCategoriesError, setGetAllOrganisationCategoriesError] = useState<ErrorType | null>(null);
    const [createCategoryError, setCreateCategoryError] = useState<ErrorType | null>(null);
    const [updateCategoryError, setUpdateCategoryError] = useState<ErrorType | null>(null);
    const [deleteCategoryError, setDeleteCategoryError] = useState<ErrorType | null>(null);
    const [createCategorySuccess, setCreateCategorySuccess] = useState<string>('');

    const onGetAllOrganisationCategories = useCallback(async () => {
        if (!organisation) return;
        setGetAllOrganisationCategoriesLoading(true);
        const req = await categoryService.getAll(organisation._id);

        if (req.code === 200) {
            setCategories(req.data);
            setGetAllOrganisationCategoriesError(null);
        } else {
            setCategories([]);
            setGetAllOrganisationCategoriesError({
                message: "Une erreur est survenue, veuillez réessayer",
            });
        }
        setGetAllOrganisationCategoriesLoading(false);
    }, [organisation, setCategories]);

    const onCreateCategory = useCallback(async (category: CreateCategoryType) => {
        if (!organisation) return;

        setCreateCategoryLoading(true);
        setCreateCategorySuccess('');
        const req = await categoryService.create({
            ...category,
            organisationID: organisation._id,
        });

        if (req.code === 201) {
            await onGetAllOrganisationCategories();
            setCreateCategoryError(null);
            setCreateCategorySuccess("La catégorie a été créée avec succès");
        } else {
            setCreateCategoryError({
                message: req.code === 400 ?
                    "Le nom de la catégorie est déjà utilisé ou donnée invalide" :
                    "Une erreur est survenue, veuillez réessayer",
            });
        }
        setCreateCategoryLoading(false);
    }, [organisation, onGetAllOrganisationCategories]);

    const onUpdateCategory = useCallback(async (categoryId: string, category: Partial<CreateCategoryType>) => {
        setUpdateCategoryLoading(true);
        const req = await categoryService.update(categoryId, category);

        if (req.code === 200) {
            await onGetAllOrganisationCategories();
            setUpdateCategoryError(null);
        } else {
            setUpdateCategoryError({
                message: req.code === 404 ?
                    "La catégorie n'existe pas" :
                    "Une erreur est survenue, veuillez réessayer",
            });
        }
        setUpdateCategoryLoading(false);
    }, [onGetAllOrganisationCategories]);

    const onDeleteCategory = useCallback(async (categoryId: string) => {
        setDeleteCategoryLoading(true);
        const req = await categoryService.delete(categoryId);

        if (req.code === 200) {
            await onGetAllOrganisationCategories();
            setDeleteCategoryError(null);
        } else {
            setDeleteCategoryError({
                message: req.code === 404 ?
                    "La catégorie n'existe pas" :
                    "Une erreur est survenue, veuillez réessayer",
            });
        }
        setDeleteCategoryLoading(false);
    }, [onGetAllOrganisationCategories]);

    return {
        createCategorySuccess,
        categories, getAllOrganisationCategoriesError,
        createCategoryError, updateCategoryError,
        deleteCategoryError, getAllOrganisationCategoriesLoading,
        createCategoryLoading, updateCategoryLoading, deleteCategoryLoading,
        onGetAllOrganisationCategories, onCreateCategory,
        onUpdateCategory, onDeleteCategory,
    }
};
